import React, { useEffect } from "react";
import {
  FaTimes, FaMoneyBill, FaCreditCard,
  FaExchangeAlt, FaClock, FaInfoCircle,
  FaGlobe
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

type SalesSummaryModalProps = {
  show: boolean;
  onClose: () => void;
  accountSummary: {
    totals: Record<string, number>;
    quantities: Record<string, number>;
    cash: number;
    pos: number;
    transfer: number;
    pending: number;
    online: number;
    grossSales: number;
    totalDiscount?: number;
    expenses: number;
  };
  date?: string;
};


function toIsoDate(dateString: string | Date): string {
  const date = new Date(dateString);

  if (isNaN(date.getTime())) { // ✅ safer check
    throw new Error("Invalid date string");
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export default function SalesSummaryModal({ show, onClose, accountSummary, date = "Today" }: SalesSummaryModalProps) {
  const navigate = useNavigate();
  const [showGrossInfo, setShowGrossInfo] = React.useState(false);
  const grossInfoRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (grossInfoRef.current && !grossInfoRef.current.contains(event.target as Node)) {
        setShowGrossInfo(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (!show) return null;

  const totalDiscount = accountSummary.totalDiscount ?? 0;
  const discountedPending = accountSummary.pending;

  const expenses = accountSummary.expenses ?? 0;
  const netSales = (
    accountSummary.grossSales - expenses -
    (discountedPending > 0 ? discountedPending : totalDiscount)
  );


  let dt = "";
  if (date === "Today") dt = toIsoDate(new Date());
  else dt = toIsoDate(date);

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      {/* Backdrop */}
      <div className="absolute inset-0" onClick={onClose} />

      {/* Modal */}
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-[95%] md:w-[500px] max-h-[85vh] overflow-y-auto p-6
          transform transition-all duration-300 ease-out scale-100 opacity-100 animate-fadeScaleIn"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
        >
          {FaTimes({ size: 14 })}
        </button>

        <h2 className="text-xl text-center font-bold text-gray-800 mb-4">{date} Sales Summary</h2>

        {/* Categories */}
        <div className="space-y-3">
          {Object.entries(accountSummary.totals)
            .filter(([key]) => ["rooms", "foods", "drinks", "laundries", "games"].includes(key))
            .map(([category, total]) => (
              <div
                key={category}
                onClick={() => navigate(`/sales/${category}?date=${dt}`)}
                className="flex justify-between items-center bg-gray-50 rounded-lg px-4 py-3 
                   hover:bg-blue-50 hover:shadow transition cursor-pointer"
              >
                <div className="flex flex-col">
                  <span className="capitalize text-gray-700 font-medium">
                    {category === "rooms"
                      ? `${accountSummary?.quantities?.rooms || 0} Rooms Booked`
                      : category === "foods"
                        ? `${accountSummary?.quantities?.foods || 0} Meals Sold`
                        : category === "drinks"
                          ? `${accountSummary?.quantities?.drinks || 0} Drinks Sold`
                          : category === "games"
                            ? `${accountSummary?.quantities?.games || 0} Games Played`
                            : category === "laundries"
                              ? `${accountSummary?.quantities?.laundries || 0} Laundry Washes`
                              : `${accountSummary?.quantities[category] || 0} ${category}`}
                  </span>
                  <span className="text-[11px] text-blue-500">Click to view details</span>
                </div>
                <span className="text-gray-900 font-semibold">₦{(total ?? 0).toLocaleString()}</span>
              </div>
            ))}
        </div>

        {/* Payment Breakdown */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-4">
          {[
            {
              label: "Cash",
              icon: <span className="text-green-600">{FaMoneyBill({})}</span>,
              value: accountSummary.cash
            },
            {
              label: "POS",
              icon: <span className="text-blue-600">{FaCreditCard({})}</span>,
              value: accountSummary.pos
            },
            {
              label: "Transfer",
              icon: <span className="text-indigo-600">{FaExchangeAlt({})}</span>,
              value: accountSummary.transfer
            },
            {
              label: "Pending",
              icon: <span className="text-amber-600">{FaClock({})}</span>,
              value: accountSummary.pending
            },
            {
              label: "Online",
              icon: <span className="text-purple-600">{FaGlobe({})}</span>,
              value: accountSummary.online
            },
          ].map((item, index) => (
            <div

              key={index}
              className="flex flex-col items-center justify-center p-2 border rounded-xl 
                 bg-gray-50 hover:bg-gray-100 transition-all duration-200 hover:shadow-sm"
            >
              <div className="flex items-center gap-1 text-gray-700 text-xs md:text-sm">
                {item.icon}
                <span className="font-medium">{item.label}</span>
              </div>
              <span className="text-sm md:text-base font-semibold text-gray-800 mt-1">
                ₦{item.value.toLocaleString()}
              </span>
            </div>
          ))}
        </div>

        <hr className="my-4" />

        {/* Totals Section */}
        <div className="space-y-2 text-sm">
          {/* Gross Sales with info icon */}
          <div className="flex flex-col text-gray-600 relative" ref={grossInfoRef}>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1">
                Gross Sales
                <span
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                  onClick={() => setShowGrossInfo(!showGrossInfo)}
                >
                  {FaInfoCircle({ size: 14 })}
                </span>
              </span>
              <span>₦{(
                accountSummary.grossSales
              ).toLocaleString()}</span>
            </div>

            {/* Smooth Fade Info Popup */}
            <div
              className={`absolute left-0 mt-1 p-2 w-64 bg-gray-50 border border-gray-200 rounded shadow text-xs text-gray-700 z-50
                transition-opacity duration-300 ${showGrossInfo ? "opacity-100 visible" : "opacity-0 invisible"}`}
            >
              <p>
                Gross Sales represents total sales, <strong>including pending payments and discount</strong>.
              </p>
            </div>
          </div>

          <div className="flex justify-between text-red-500">
            <span>Total Discount:</span>
            <span>- ₦{totalDiscount.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-red-500">
            <span>Expenses:</span>
            <span>- ₦{expenses.toLocaleString()}</span>
          </div>
          <div className="flex justify-between font-semibold text-gray-900 text-base mt-2">
            <span>Net Sales:</span>
            <span>₦{(
              netSales
            ).toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
}